// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { cartLineContainerStyles } from "@steiner/App/Components/Cart/CartLineContainer";
import { avoidBreakInsidePrintElement, color, getSystemColor, xScreen } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

cartLineContainerStyles.container = {
    css: css`
        display: grid;
        grid-template-columns: 64px minmax(0, 1fr) minmax(0, 124px) minmax(0, 180px) minmax(0, 124px) minmax(0, 150px) minmax(0, 36px);
        &[data-has-qty-shipped="true"] {
            grid-template-columns: 64px minmax(0, 1fr) minmax(0, 180px) minmax(0, 100px) minmax(0, 100px) minmax(0, 150px) minmax(0, 36px);
        }

        grid-column-gap: 12px;

        width: 100%;
        background: ${color("common.background")};
        border-bottom: 1px solid ${color("common.border")};
        padding: 12px;

        @media print {
            width: 100%;
            content: "";
            display: table;
            clear: both;
            -webkit-column-break-inside: avoid;
            page-break-inside: avoid;
            break-inside: avoid;
            border-top: 1px solid ${getSystemColor("common.border")};
        }

        ${avoidBreakInsidePrintElement}

        ${xScreen("lte", "md")} {
            grid-row-gap: 12px;
            padding-bottom: 0;
            grid-template-columns:
                99px minmax(0, 1fr) minmax(0, 330px) minmax(0, 330px) minmax(0, 330px) minmax(0, 330px) minmax(0, 16px)
                minmax(0, 1fr);

            &[data-has-qty-shipped="true"] {
                    grid-template-columns: 99px minmax(0, 1fr) minmax(0, 180px) minmax(0, 100px) minmax(0, 100px) minmax(0, 100px) minmax(0, 36px);
                }
        }

        ${xScreen("lte", "xs")} {
            &:last-child {
                border-bottom: 0;
            }
        }
    `,
};

cartLineContainerStyles.image = {
    css: css`
        grid-column: 1 / 2;
        grid-row: 1 / 4;
        ${xScreen("lte", "md")} {
            grid-row: 1 / 2;
            grid-column: 1 / 2;
        }
        @media print {
            display: block;
            max-width: 99px;
            max-height: 99px;
            margin-right: 12px;
            float: left;
        }
    `,
};

cartLineContainerStyles.description = {
    css: css`
        grid-column: 2 / 3;
        grid-row: 1 / 2;
        position: relative;
        display: flex;
        flex-direction: column;
        row-gap: 8px;

        ${xScreen("lte", "md")} {
            grid-column: 2 / 7;
            margin-left: 4px;
        }

        @media print {
            &&& {
                min-width: calc(100% - 130px);
                min-width: calc(100% - 130px);
                min-height: 120px;
            }
        }
    `,
};

cartLineContainerStyles.price = {
    css: css`
        grid-column: 4 / 5;
        grid-row: 1 / 2;
        position: relative;
        padding-top: 3px;
        padding-left: 12px;
        &[data-has-qty-shipped="true"] {
            grid-column: 3 / 4;
        }
        ${xScreen("lte", "md")} {
            grid-row: 2 / 3;
            grid-column: 1 / 4;
            margin-right: 12px;
            padding: 0;
            
            &[data-has-qty-shipped="true"] {
                grid-column: 1 / 3;
                margin-right: 12px;
            }
        }

        ${xScreen("lte", "md")} {
            margin: 4px 0;
        }

        ${xScreen("lte", "sm")} {
            &[data-has-qty-shipped="true"] {
                grid-column: 1 / 8;
            }
        }

        @media print {
            min-width: 23%;
            max-width: 23%;
            min-height: 50px;
            float: left;
            margin-top: 20px;
        }
    `,
};

cartLineContainerStyles.qty = {
    css: css`
        grid-column: 5 / 6;
        grid-row: 1 / 2;
        padding-left: 12px;
        &[data-has-qty-shipped="true"] {
            grid-column: 4 / 5;
        }

        ${xScreen("lte", "md")} {
            grid-row: 2 / 3;
            grid-column: 5/ 6;
            margin-right: 12px;
            padding: 0;
            
            &[data-has-qty-shipped="true"] {
                grid-column: 3 / 4;
                margin-right: 12px;
            }
        }

        ${xScreen("lte", "sm")} {
            grid-row: 3 / 4;
            grid-column: 1/ 4;

            &[data-has-qty-shipped="true"] {
                grid-column: 1 / 2;
                margin-right: 12px;
            }
        }

        @media print {
            margin-right: 0;
            min-width: 23%;
            max-width: 23%;
            min-height: 50px;
            float: left;
            margin-top: 20px;
        }
    `,
};

cartLineContainerStyles.qtyShipped = {
    css: css`
        grid-column: 5 / 6;
        grid-row: 1 / 2;
        padding-left: 12px;
        display: none;
        &[data-has-qty-shipped="true"] {
            display: block;
            grid-column: 5 / 6;
        }

        ${xScreen("lte", "md")} {
            grid-row: 2 / 3;
            grid-column: 6/ 7;
            margin-right: 12px;
            padding: 0;
            
            &[data-has-qty-shipped="true"] {
                display: block;
                grid-column: 4 / 5;
                margin-right: 12px;
            }
        }

        ${xScreen("lt", "sm")} {
            grid-row: 3 / 4;
            grid-column: 5/ 8;

            &[data-has-qty-shipped="true"] {
                grid-column: 4 / 8;
            }
        }

        @media print {
            margin-right: 0;
            min-width: 23%;
            max-width: 23%;
            min-height: 50px;
            float: left;
            margin-top: 20px;
        }
    `,
};

cartLineContainerStyles.total = {
    css: css`
        grid-column: 6 / 7;
        grid-row: 1 / 2;
        position: relative;
        padding-top: 3px;
        padding-left: 12px;
        &[data-has-qty-shipped="true"] {
            grid-column: 6 / 7;
        }

        ${xScreen("lte", "md")} {
            grid-row: 2 / 3;
            grid-column: 6 / 9;
            padding: 0;
            display: flex;
            justify-content: center;
            
            &[data-has-qty-shipped="true"] {
                grid-column: 5 / 7;
                justify-content: center;
            }
        }

        ${xScreen("lte", "sm")} {
            grid-row: 3 / 4;
            grid-column: 5 / 8;
            justify-content: flex-start;

            &[data-has-qty-shipped="true"] {
                grid-column: 1 / 7;
                 grid-row: 4 / 5;
                justify-content: center;
            }
        }

        @media print {
            min-width: 23%;
            max-width: 23%;
            min-height: 50px;
            float: right;
            margin-top: 20px;
        }
    `,
};

cartLineContainerStyles.remove = {
    css: css`
        grid-column: 7 / 8;
        grid-row: 1 / 2;
        padding-top: 6px;
        display: flex;
        justify-content: flex-end;
        align-items: baseline;

        ${xScreen("lte", "md")} {
            grid-row: 1 / 2;
            grid-column: 7 / 9;
            
            &[data-has-qty-shipped="true"] {
                grid-column: 6 / 7;
            }
        }

        @media print {
            display: none;
        }
    `,
};

cartLineContainerStyles.notes = {
    css: css`
        grid-column: 2 / 7;
        grid-row: 3 / 4;
        position: relative;

        ${xScreen("lte", "md")} {
            display: none;
        }

        @media print {
            display: block;
            width: 100%;
            float: left;
            margin-top: 20px;
        }
    `,
};

cartLineContainerStyles.mobileNotes = {
    css: css`
        ${xScreen("gt", "lg")} {
            display: none;
        }

        grid-column: 1 / 9;
        grid-row: 3 / 3;
        margin-top: 4px;
        
        &[data-has-qty-shipped="true"] {
            grid-column: 1 / 8;
        }

        ${xScreen("lte", "md")} {
            margin-bottom: 15px;
        }

        ${xScreen("lte", "sm")} {
            grid-row: 4 / 4;

            &[data-has-qty-shipped="true"] {
                grid-row: 5 / 6;
            }
        }

        @media print {
            display: none;
        }
    `,
};

cartLineContainerStyles.actions = {
    css: css`
        grid-column: 2 / 7;
        grid-row: 2 / 2;
        &:empty {
            display: none;
        }

        ${xScreen("lte", "md")} {
            grid-column: 1 / 9;
            grid-row: 3 / 3;
            
            &[data-has-qty-shipped="true"] {
                grid-column: 1 / 8;
            }
        }

        ${xScreen("lt", "sm")} {
            grid-row: 4 / 4;
        }
    `,
};

cartLineContainerStyles.inventory = {
    css: css`
        grid-column: 3 / 4;
        grid-row: 1 / 2;
        position: relative;
        padding-top: 3px;
        padding-left: 12px;
        &[data-has-qty-shipped="true"] {
            display: none;
        }

        ${xScreen("lte", "md")} {
            grid-row: 2 / 3;
            grid-column: 4 / 5;
            margin-right: 12px;
            padding: 0;
        }

        ${xScreen("lte", "sm")} {
            grid-row: 2 / 3;
            grid-column: 5 / 9;
            margin-right: 0;
            padding: 0;
            margin-top: 12px;
        }

        @media print {
            min-width: 23%;
            max-width: 23%;
            min-height: 50px;
            float: left;
            margin-top: 20px;
        }
    `,
};
